import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiSave, FiSearch, FiArrowLeft,
  FiCheck, FiEye, FiBook, FiPrinter
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import { formatAmount } from '../../utils/documentNumbering';
import {
  createTransaction, updateTransaction, deleteTransaction, getTransactions
} from '../../utils/transactionHelper';
import { generateSubTransactions } from '../../utils/subTransactionHelper';
import PageLayout from '../../components/PageLayout';
import { isFutureDate, isLargeTransaction } from '../../utils/validationHelpers';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useTableSort from '../../hooks/useTableSort';
import useOpenFromReport from '../../hooks/useOpenFromReport';
import AttachmentPanel from '../../components/AttachmentPanel';
import AccountSelect from '../../components/AccountSelect';
import {
  ActionBar, SearchBox, FilterGroup, StatusFilterSelect, TranAddButton, LoadingMessage,
  TranEmptyState, TableContainer, Table, Th, Tr, Td, StatusBadge, ActionGroup, IconBtn,
  SummaryBar, FormHeader, BackButton, FormTitle, FormActions, SaveButton, FormCard,
  SectionTitle, FormGrid, FormGroup, Label, Input, Select, TextArea, InfoGrid, InfoItem,
  NotesText, LineItemsHeader, AddLineButton, LineTableContainer, LineTable, LineTh, LineTd,
  LineInput, RemoveLineBtn, TotalsSection, TotalsTable, TotalRow, SortableTh, SortContext
} from '../../components/SharedStyles';
import { addPrintBrand } from '../../utils/printBranding';

const TRAN_TYPE_CODE = 'CB';

// A line is a contra entry: pick an account, then put the amount in Debit
// (cash out / payment) or Credit (cash in / receipt). Cash account auto-mirrors.
const EMPTY_LINE = { accountId: '', accountCode: '', accountName: '', partyType: '', partyId: '', narration: '', debitAmount: 0, creditAmount: 0 };

const STATUS_COLORS = { Draft: colors.warning.main, Posted: colors.success.main };

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const CashBook = () => {
  const { activeCompany, activeFinancialYear, currentUser, activeBranch } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('transactions', 'cashBook');
  const showBranchCol = activeBranch && activeBranch.id === 'all';

  const [viewMode, setViewMode] = useState('list');
  const [records, setRecords] = useState([]);
  const sort = useTableSort('docDate', 'desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [opening, setOpening] = useState(null); // cash account balance before docDate

  const [formData, setFormData] = useState({
    docNumber: '', docDate: new Date().toISOString().split('T')[0],
    cashAccountId: '', cashAccountCode: '', cashAccountName: '',
    narration: '', notes: '', status: 'Draft'
  });
  const [lineItems, setLineItems] = useState([{ ...EMPTY_LINE }]);

  const accountsById = useMemo(() => {
    const map = {};
    accounts.forEach(a => { map[a.id] = a; });
    return map;
  }, [accounts]);

  // Cash accounts = postable Level-5 leaves under Cash in Hand (11101xxx).
  const cashAccounts = useMemo(() => accounts
    .filter(a => a.isActive !== false && a.isPostable !== false && String(a.code || '').startsWith('11101'))
    .sort((a, b) => String(a.code).localeCompare(String(b.code))), [accounts]);

  const loadAccounts = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const snap = await getDocs(collection(db, 'companies', activeCompany.id, 'accounts'));
      setAccounts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error('Error loading accounts:', e); }
  }, [activeCompany]);

  const loadRecords = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      setLoading(true);
      const data = await getTransactions(activeCompany.id, { tranTypeCode: TRAN_TYPE_CODE, activeYearOnly: true, ...(activeBranch && activeBranch.id !== 'all' ? { branchId: activeBranch.id } : {}) });
      setRecords(data);
    } catch (error) {
      console.error('Error loading records:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, activeBranch]);

  useEffect(() => { loadRecords(); loadAccounts(); }, [loadRecords, loadAccounts]);

  // Preselect the first cash account (Cash in Hand) for new vouchers.
  useEffect(() => {
    if (viewMode === 'form' && !editingRecord && !formData.cashAccountId && cashAccounts.length > 0) {
      const c = cashAccounts[0];
      setFormData(prev => ({ ...prev, cashAccountId: c.id, cashAccountCode: c.code, cashAccountName: c.name }));
    }
  }, [viewMode, editingRecord, formData.cashAccountId, cashAccounts]);

  // Compute opening balance: posted ledger of the cash account strictly before docDate.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (viewMode !== 'form' || !formData.cashAccountId || !formData.docDate) { setOpening(null); return; }
      try {
        const snap = await getDocs(query(
          collection(db, 'companies', activeCompany.id, 'subTransactions'),
          where('accountId', '==', formData.cashAccountId)
        ));
        let bal = 0;
        snap.docs.forEach(d => {
          const t = d.data();
          if (t.status !== 'Posted') return;
          if ((t.docDate || '') >= formData.docDate) return; // strictly before this day
          if (activeBranch && activeBranch.id !== 'all' && t.branchId && t.branchId !== activeBranch.id) return;
          bal += (t.debitAmount || 0) - (t.creditAmount || 0);
        });
        if (!cancelled) setOpening(r2(bal));
      } catch (e) { if (!cancelled) setOpening(null); }
    };
    run();
    return () => { cancelled = true; };
  }, [viewMode, formData.cashAccountId, formData.docDate, activeCompany, activeBranch]);

  const filteredRecords = useMemo(() => {
    let filtered = records;
    if (statusFilter !== 'All') filtered = filtered.filter(r => r.status === statusFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.docNumber?.toLowerCase().includes(q) || r.narration?.toLowerCase().includes(q) || r.cashAccountName?.toLowerCase().includes(q)
      );
    }
    return sort.sortItems(filtered);
  }, [records, statusFilter, searchQuery, sort]);

  const totals = useMemo(() => {
    const payments = r2(lineItems.reduce((s, l) => s + (Number(l.debitAmount) || 0), 0)); // Dr lines = cash out
    const receipts = r2(lineItems.reduce((s, l) => s + (Number(l.creditAmount) || 0), 0)); // Cr lines = cash in
    const net = r2(receipts - payments);
    const closing = opening != null ? r2(opening + net) : null;
    return { payments, receipts, net, closing };
  }, [lineItems, opening]);

  const handleHeaderChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleLineChange = (index, field, value) => {
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'debitAmount' && Number(value) > 0) updated[index].creditAmount = 0;
      if (field === 'creditAmount' && Number(value) > 0) updated[index].debitAmount = 0;
      return updated;
    });
  };

  const handleLineAccount = (index, account) => {
    const full = account?.id ? accountsById[account.id] : null;
    setLineItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        accountId: account?.id || '',
        accountCode: account?.code || '',
        accountName: account?.name || '',
        partyType: full?.linkedEntityType || '',
        partyId: full?.linkedEntityId || ''
      };
      return updated;
    });
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (index) => { if (lineItems.length <= 1) return; setLineItems(prev => prev.filter((_, i) => i !== index)); };

  const resetForm = () => {
    setFormData({ docNumber: '', docDate: new Date().toISOString().split('T')[0], cashAccountId: '', cashAccountCode: '', cashAccountName: '', narration: '', notes: '', status: 'Draft' });
    setLineItems([{ ...EMPTY_LINE }]);
    setEditingRecord(null);
    setOpening(null);
  };

  const handleCreate = () => { resetForm(); setViewMode('form'); };

  const handleEdit = (rec) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setEditingRecord(rec);
    setFormData({
      docNumber: rec.docNumber || '', docDate: rec.docDate || new Date().toISOString().split('T')[0],
      cashAccountId: rec.cashAccountId || '', cashAccountCode: rec.cashAccountCode || '', cashAccountName: rec.cashAccountName || '',
      narration: rec.narration || '', notes: rec.notes || '', status: rec.status || 'Draft'
    });
    setLineItems(rec.lines?.length > 0 ? rec.lines.map(l => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }]);
    setViewMode('form');
  };

  const handleView = (rec) => { setViewingRecord(rec); setViewMode('view'); };
  useOpenFromReport(records, handleView);

  const handleSave = async (saveStatus) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.cashAccountId) { alert('Please select a cash account.'); return; }
    if (!activeFinancialYear?.id) { alert('No active financial year. Set one in Settings > Financial Year.'); return; }

    const validLines = lineItems.filter(l => l.accountId && (Number(l.debitAmount) > 0 || Number(l.creditAmount) > 0));
    if (validLines.length < 1) { alert('Add at least one line with an account and a Debit or Credit amount.'); return; }
    if (validLines.some(l => l.accountId === formData.cashAccountId)) { alert('A line cannot use the same cash account selected on top.'); return; }

    // One voucher per day per cash account.
    const dup = records.find(r => r.id !== editingRecord?.id && r.docDate === formData.docDate
      && r.cashAccountId === formData.cashAccountId
      && (!activeBranch || activeBranch.id === 'all' || r.branchId === activeBranch.id));
    if (dup) { alert(`A Cash Book voucher (${dup.docNumber}) already exists for this date and cash account. Edit that one instead.`); return; }

    if (isFutureDate(formData.docDate)) { if (!window.confirm('Document date is in the future. Continue?')) return; }

    const payments = r2(validLines.reduce((s, l) => s + (Number(l.debitAmount) || 0), 0));
    const receipts = r2(validLines.reduce((s, l) => s + (Number(l.creditAmount) || 0), 0));
    if (receipts + payments <= 0) { alert('Enter at least one non-zero amount.'); return; }
    const netCash = r2(receipts - payments);
    const balancedDebit = r2(payments + Math.max(netCash, 0));
    const balancedCredit = r2(receipts + Math.max(-netCash, 0));

    if (isLargeTransaction(Math.max(balancedDebit, balancedCredit))) {
      if (!window.confirm('This is a large transaction. Verify the amount. Continue?')) return;
    }

    setSaving(true);
    try {
      const calculatedLines = validLines.map((l, i) => ({
        accountId: l.accountId, accountCode: l.accountCode || '', accountName: l.accountName,
        partyType: l.partyType || '', partyId: l.partyId || '',
        narration: l.narration || '', debitAmount: r2(l.debitAmount), creditAmount: r2(l.creditAmount), lineNumber: i + 1
      }));
      const status = saveStatus || 'Draft';
      const data = {
        tranTypeCode: TRAN_TYPE_CODE, docDate: formData.docDate,
        cashAccountId: formData.cashAccountId, cashAccountCode: formData.cashAccountCode, cashAccountName: formData.cashAccountName,
        branchId: activeBranch?.id || null, branchName: activeBranch?.branchName || null,
        narration: formData.narration, notes: formData.notes, status,
        financialYearId: activeFinancialYear.id,
        lines: calculatedLines,
        totalReceipts: receipts, totalPayments: payments, netCash,
        totalDebit: balancedDebit, totalCredit: balancedCredit
      };

      let savedDocId = editingRecord?.id;
      let savedDocNumber = editingRecord?.docNumber;
      let newTranData = null;
      // manualLines = each contra line as entered + the cash account taking the
      // net opposite side so the voucher is always balanced.
      const buildManualLines = (docNo) => {
        const lines = calculatedLines.map(l => ({
          accountId: l.accountId, accountCode: l.accountCode, accountName: l.accountName,
          debitAmount: l.debitAmount, creditAmount: l.creditAmount,
          description: l.narration || formData.narration,
          partyId: l.partyId || null, partyType: l.partyType || null
        }));
        if (netCash !== 0) {
          lines.push({
            accountId: formData.cashAccountId, accountCode: formData.cashAccountCode, accountName: formData.cashAccountName,
            debitAmount: netCash > 0 ? netCash : 0,
            creditAmount: netCash < 0 ? -netCash : 0,
            description: `Cash ${netCash > 0 ? 'received' : 'paid'} — ${docNo}`
          });
        }
        return lines;
      };
      // On EDIT the ledger is replaced inside the same save (a posted Cash Book
      // re-saved as posted used to lose its ledger lines).
      if (editingRecord && status === 'Posted') data.subTransactionLines = buildManualLines(editingRecord.docNumber);
      if (editingRecord) {
        await updateTransaction(activeCompany.id, editingRecord.id, data, currentUser.uid);
      } else {
        newTranData = await createTransaction(activeCompany.id, data, currentUser.uid);
        savedDocId = newTranData.id;
        savedDocNumber = newTranData.docNumber;
      }

      if (!editingRecord && status === 'Posted') {
        const manualLines = buildManualLines(savedDocNumber);
        await generateSubTransactions(activeCompany.id, {
          transactionId: savedDocId, tranTypeCode: TRAN_TYPE_CODE, docNumber: savedDocNumber,
          docDate: formData.docDate, financialYearId: activeFinancialYear.id,
          branchId: activeBranch?.id || null, branchName: activeBranch?.branchName || null,
          status: 'Posted', manualLines
        }, currentUser.uid);
      }

      const savedRecord = editingRecord
        ? { ...editingRecord, ...data, id: savedDocId, docNumber: savedDocNumber, status }
        : { ...newTranData, status };
      setRecords(prev => editingRecord ? prev.map(r => r.id === savedDocId ? savedRecord : r) : [savedRecord, ...prev]);
      setViewMode('list');
      resetForm();
      loadRecords().catch(err => console.error('Background refresh failed:', err));
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rec) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (!window.confirm(`Delete ${rec.docNumber}?`)) return;
    try {
      // ACC-04 fix (2026-09-30): deleteTransaction() runs the safety checks FIRST
      // (linked documents, receipts, FBR) and only then removes ledger + stock rows.
      // Wiping them here beforehand left a blocked document with no accounting.
      await deleteTransaction(activeCompany.id, rec.id);
      await loadRecords();
    } catch (error) { console.error('Error deleting:', error); alert('Error deleting record'); }
  };

  const cur = activeCompany?.currencyCode || 'PKR';

  const handlePrint = (rec) => {
    const data = rec || viewingRecord;
    if (!data) return;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>${data.docNumber}</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:40px;color:#1A1A1A}
        .header{display:flex;justify-content:space-between;margin-bottom:24px;border-bottom:2px solid #800020;padding-bottom:12px}
        .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px}
        .info-item label{font-weight:600;font-size:12px;color:#666;display:block}
        .info-item span{font-size:14px}
        table{width:100%;border-collapse:collapse;margin-bottom:16px}
        th{background:#800020;color:white;padding:8px;text-align:left;font-size:12px}
        td{padding:7px 8px;border-bottom:1px solid #ddd;font-size:13px}
        .amount{text-align:right;font-family:'Consolas',monospace}
        .totals{float:right;width:340px} .totals td{border:none;padding:4px 10px}
        .totals tr:last-child{font-weight:bold;border-top:2px solid #800020}
        @media print{body{margin:20px}}
      </style></head><body>
      <div class="header">
        <div>
          <p style="margin:0;font-weight:700;font-size:20px;color:#800020">${activeCompany?.companyName || 'Company'}</p>
          ${activeCompany?.address ? `<p style="margin:2px 0;font-size:12px;color:#666">${activeCompany.address}${activeCompany.city ? ', ' + activeCompany.city : ''}</p>` : ''}
        </div>
        <div style="text-align:right">
          <h1 style="margin:0;font-size:22px;color:#800020">CASH BOOK</h1>
          <p style="font-size:18px;font-weight:600;margin:4px 0">${data.docNumber}</p>
          <p style="margin:2px 0;font-size:13px">Date: ${data.docDate}</p>
          <p style="margin:2px 0;font-size:13px">Status: ${data.status}</p>
        </div>
      </div>
      <div class="info-grid">
        <div class="info-item"><label>Cash Account</label><span>${data.cashAccountName || '-'}</span></div>
        ${data.branchName ? `<div class="info-item"><label>Branch</label><span>${data.branchName}</span></div>` : ''}
        <div class="info-item" style="grid-column:span 2"><label>Narration</label><span>${data.narration || '-'}</span></div>
      </div>
      <table><thead><tr><th>#</th><th>Account</th><th>Narration</th>
        <th class="amount">Debit (Out)</th><th class="amount">Credit (In)</th></tr></thead>
        <tbody>${(data.lines || []).map((l, i) => `<tr><td>${i + 1}</td><td>${l.accountName}${l.partyType ? ' [P]' : ''}</td>
          <td>${l.narration || '-'}</td><td class="amount">${l.debitAmount > 0 ? formatAmount(l.debitAmount) : '-'}</td>
          <td class="amount">${l.creditAmount > 0 ? formatAmount(l.creditAmount) : '-'}</td></tr>`).join('')}</tbody></table>
      <div class="totals"><table>
        <tr><td>Total Receipts (In)</td><td class="amount">${cur} ${formatAmount(data.totalReceipts)}</td></tr>
        <tr><td>Total Payments (Out)</td><td class="amount">${cur} ${formatAmount(data.totalPayments)}</td></tr>
        <tr><td>Net to Cash</td><td class="amount">${cur} ${formatAmount(data.netCash)}</td></tr>
      </table></div>
      ${data.notes ? `<div style="margin-top:30px;clear:both"><strong>Notes:</strong><p>${data.notes}</p></div>` : ''}
    </body></html>`);
    addPrintBrand(w);
    w.document.close(); w.print();
  };

  const renderListView = () => (
    <>
      <ActionBar>
        <SearchBox><FiSearch />
          <input type="text" placeholder="Search cash book..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </SearchBox>
        <FilterGroup>
          <StatusFilterSelect value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="All">All Status</option>
            <option value="Draft">Draft</option>
            <option value="Posted">Posted</option>
          </StatusFilterSelect>
          {canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> New Cash Book</TranAddButton>}
        </FilterGroup>
      </ActionBar>
      {loading ? <LoadingMessage>Loading...</LoadingMessage> : filteredRecords.length === 0 ? (
        <TranEmptyState><FiBook size={48} /><h3>No Cash Book Vouchers</h3>
          <p>{records.length === 0 ? 'Create your first daily cash book' : 'No records match filters'}</p>
          {records.length === 0 && canEdit && <TranAddButton onClick={handleCreate}><FiPlus /> Create Cash Book</TranAddButton>}
        </TranEmptyState>
      ) : (
        <TableContainer><SortContext.Provider value={sort.ctxValue}><Table><thead><tr>
          <SortableTh field="docNumber">Doc No</SortableTh><SortableTh field="docDate">Date</SortableTh>
          {showBranchCol && <SortableTh field="branchName">Branch</SortableTh>}
          <SortableTh field="cashAccountName">Cash Account</SortableTh>
          <SortableTh field="totalReceipts" style={{ textAlign: 'right' }}>Receipts</SortableTh>
          <SortableTh field="totalPayments" style={{ textAlign: 'right' }}>Payments</SortableTh>
          <SortableTh field="netCash" style={{ textAlign: 'right' }}>Net</SortableTh>
          <SortableTh field="status" style={{ textAlign: 'center' }}>Status</SortableTh>
          <Th style={{ textAlign: 'center' }}>Actions</Th>
        </tr></thead><tbody>
          {filteredRecords.map(rec => (
            <Tr key={rec.id}>
              <Td><strong>{rec.docNumber}</strong></Td><Td>{rec.docDate}</Td>
              {showBranchCol && <Td>{rec.branchName || '-'}</Td>}
              <Td>{rec.cashAccountName || '-'}</Td>
              <Td style={{ textAlign: 'right', color: '#1565C0' }}>{formatAmount(rec.totalReceipts)}</Td>
              <Td style={{ textAlign: 'right', color: '#C62828' }}>{formatAmount(rec.totalPayments)}</Td>
              <Td style={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(rec.netCash)}</Td>
              <Td style={{ textAlign: 'center' }}><StatusBadge $color={STATUS_COLORS[rec.status]}>{rec.status}</StatusBadge></Td>
              <Td style={{ textAlign: 'center' }}><ActionGroup>
                <IconBtn title="View" onClick={() => handleView(rec)}><FiEye /></IconBtn>
                {canChange && <IconBtn title="Edit" onClick={() => handleEdit(rec)}><FiEdit2 /></IconBtn>}
                <IconBtn title="Print" onClick={() => handlePrint(rec)}><FiPrinter /></IconBtn>
                {canDelete && <IconBtn title="Delete" $danger onClick={() => handleDelete(rec)}><FiTrash2 /></IconBtn>}
              </ActionGroup></Td>
            </Tr>
          ))}
        </tbody></Table></SortContext.Provider></TableContainer>
      )}
      <SummaryBar>
        <span>Total: <strong>{filteredRecords.length}</strong> voucher(s)</span>
        <span>Draft: <strong>{records.filter(r => r.status === 'Draft').length}</strong> | Posted: <strong>{records.filter(r => r.status === 'Posted').length}</strong></span>
      </SummaryBar>
    </>
  );

  const renderViewMode = () => {
    if (!viewingRecord) return null;
    const rec = viewingRecord;
    return (
      <>
        <FormHeader>
          <BackButton onClick={() => { setViewMode('list'); setViewingRecord(null); }}><FiArrowLeft /> Back to List</BackButton>
          <FormTitle>{rec.docNumber}<StatusBadge $color={STATUS_COLORS[rec.status]} style={{ marginLeft: 12 }}>{rec.status}</StatusBadge></FormTitle>
          <FormActions>
            {canChange && <SaveButton onClick={() => handleEdit(rec)}><FiEdit2 /> Edit</SaveButton>}
            <SaveButton $secondary onClick={() => handlePrint(rec)}><FiPrinter /> Print</SaveButton>
          </FormActions>
        </FormHeader>
        <FormCard>
          <SectionTitle>Cash Book Details</SectionTitle>
          <InfoGrid>
            <InfoItem><label>Doc No</label><span>{rec.docNumber}</span></InfoItem>
            <InfoItem><label>Date</label><span>{rec.docDate}</span></InfoItem>
            <InfoItem><label>Cash Account</label><span>{rec.cashAccountName || '-'}</span></InfoItem>
            <InfoItem><label>Net to Cash</label><span>{cur} {formatAmount(rec.netCash)}</span></InfoItem>
            <InfoItem style={{ gridColumn: 'span 2' }}><label>Narration</label><span>{rec.narration || '-'}</span></InfoItem>
          </InfoGrid>
        </FormCard>
        <FormCard>
          <SectionTitle>Entries</SectionTitle>
          <TableContainer><Table><thead><tr>
            <Th>#</Th><Th>Account</Th><Th>Narration</Th>
            <Th style={{ textAlign: 'right' }}>Debit (Out)</Th><Th style={{ textAlign: 'right' }}>Credit (In)</Th>
          </tr></thead><tbody>
            {(rec.lines || []).map((line, i) => (
              <Tr key={i}><Td>{i + 1}</Td>
                <Td>{line.accountName} {line.partyType && <PartyBadge>P</PartyBadge>}</Td>
                <Td>{line.narration || '-'}</Td>
                <Td style={{ textAlign: 'right' }}>{line.debitAmount > 0 ? formatAmount(line.debitAmount) : '-'}</Td>
                <Td style={{ textAlign: 'right' }}>{line.creditAmount > 0 ? formatAmount(line.creditAmount) : '-'}</Td>
              </Tr>
            ))}
          </tbody></Table></TableContainer>
          <TotalsSection><TotalsTable><tbody>
            <tr><td>Total Receipts (In)</td><td>{formatAmount(rec.totalReceipts)}</td></tr>
            <tr><td>Total Payments (Out)</td><td>{formatAmount(rec.totalPayments)}</td></tr>
            <TotalRow><td>Net to Cash ({cur})</td><td>{formatAmount(rec.netCash)}</td></TotalRow>
          </tbody></TotalsTable></TotalsSection>
        </FormCard>
        {rec.notes && <FormCard><SectionTitle>Notes</SectionTitle><NotesText>{rec.notes}</NotesText></FormCard>}
        <AttachmentPanel transactionId={rec?.id} attachment={rec?.attachment} canEdit={false} />
      </>
    );
  };

  const renderFormView = () => (
    <>
      <FormHeader>
        <BackButton onClick={() => { setViewMode('list'); resetForm(); }}><FiArrowLeft /> Back to List</BackButton>
        <FormTitle>{editingRecord ? `Edit: ${editingRecord.docNumber}` : 'New Cash Book'}</FormTitle>
        <FormActions>
          <SaveButton $secondary onClick={() => { setViewMode('list'); resetForm(); }}><FiX /> Cancel</SaveButton>
          {canEdit && !(editingRecord && editingRecord.status && editingRecord.status !== 'Draft' && editingRecord.status !== 'Cancelled') && <SaveButton onClick={() => handleSave('Draft')} disabled={saving}><FiSave /> {saving ? 'Saving...' : 'Save Draft'}</SaveButton>}
          {canEdit && (formData.status === 'Draft' || !editingRecord) && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> Save & Post</SaveButton>
          )}
          {/* ACC-05: editing a posted document keeps it posted (no silent drop to Draft) */}
          {canEdit && (editingRecord && editingRecord.status && editingRecord.status !== 'Draft' && editingRecord.status !== 'Cancelled') && (
            <SaveButton $approve onClick={() => handleSave('Posted')} disabled={saving}><FiCheck /> {saving ? 'Saving...' : 'Save Changes'}</SaveButton>
          )}
        </FormActions>
      </FormHeader>
      <FormCard>
        <SectionTitle>Cash Book</SectionTitle>
        <FormGrid>
          <FormGroup><Label>Doc No</Label><Input value={editingRecord?.docNumber || 'Auto-generated'} disabled /></FormGroup>
          <FormGroup><Label>Date *</Label><Input type="date" value={formData.docDate} onChange={e => handleHeaderChange('docDate', e.target.value)} disabled={!!editingRecord} /></FormGroup>
          <FormGroup>
            <Label>Cash Account *</Label>
            <Select value={formData.cashAccountId} disabled={!!editingRecord} onChange={e => {
              const c = cashAccounts.find(a => a.id === e.target.value);
              setFormData(prev => ({ ...prev, cashAccountId: c?.id || '', cashAccountCode: c?.code || '', cashAccountName: c?.name || '' }));
            }}>
              <option value="">-- Select Cash Account --</option>
              {cashAccounts.map(a => <option key={a.id} value={a.id}>{a.code} - {a.name}</option>)}
            </Select>
          </FormGroup>
          <FormGroup><Label>Opening Balance</Label><Input value={opening != null ? `${cur} ${formatAmount(opening)}` : '—'} disabled /></FormGroup>
          <FormGroup $span2><Label>Narration</Label><Input value={formData.narration} onChange={e => handleHeaderChange('narration', e.target.value)} placeholder="Day's cash book narration (optional)" /></FormGroup>
        </FormGrid>
      </FormCard>
      <FormCard>
        <LineItemsHeader>
          <SectionTitle style={{ margin: 0 }}>Entries — Debit = cash out (payment), Credit = cash in (receipt)</SectionTitle>
          <AddLineButton onClick={addLine}><FiPlus /> Add Row</AddLineButton>
        </LineItemsHeader>
        <LineTableContainer><LineTable><thead><tr>
          <LineTh style={{ width: 40, minWidth: 40, maxWidth: 40 }}>#</LineTh>
          <LineTh style={{ width: 300, minWidth: 300, maxWidth: 300 }}>Account / Party</LineTh>
          <LineTh style={{ width: 200, minWidth: 200, maxWidth: 200 }}>Narration</LineTh>
          <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Debit (Out)</LineTh>
          <LineTh style={{ width: 150, minWidth: 150, maxWidth: 150 }}>Credit (In)</LineTh>
          <LineTh style={{ width: 40, minWidth: 40, maxWidth: 40 }}></LineTh>
        </tr></thead><tbody>
          {lineItems.map((line, index) => (
            <tr key={index}>
              <LineTd style={{ textAlign: 'center', color: colors.text.secondary }}>{index + 1}</LineTd>
              <LineTd>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <AccountSelect companyId={activeCompany?.id} value={line.accountId} onChange={(account) => handleLineAccount(index, account)} />
                  </div>
                  {line.partyType && <PartyBadge title={`Party account (${line.partyType})`}>P</PartyBadge>}
                </div>
              </LineTd>
              <LineTd><LineInput value={line.narration} onChange={e => handleLineChange(index, 'narration', e.target.value)} placeholder="Line narration" /></LineTd>
              <LineTd><LineInput type="number" min="0" step="0.01" value={line.debitAmount} onChange={e => handleLineChange(index, 'debitAmount', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd><LineInput type="number" min="0" step="0.01" value={line.creditAmount} onChange={e => handleLineChange(index, 'creditAmount', Number(e.target.value))} style={{ textAlign: 'right' }} /></LineTd>
              <LineTd style={{ textAlign: 'center' }}>{lineItems.length > 1 && <RemoveLineBtn onClick={() => removeLine(index)}><FiX /></RemoveLineBtn>}</LineTd>
            </tr>
          ))}
        </tbody></LineTable></LineTableContainer>
        <TotalsSection><TotalsTable><tbody>
          <tr><td>Total Receipts / Credit (In)</td><td>{formatAmount(totals.receipts)}</td></tr>
          <tr><td>Total Payments / Debit (Out)</td><td>{formatAmount(totals.payments)}</td></tr>
          <tr><td>Net to Cash</td><td>{formatAmount(totals.net)}</td></tr>
          <TotalRow><td>Closing Balance ({cur})</td><td>{totals.closing != null ? formatAmount(totals.closing) : '—'}</td></TotalRow>
        </tbody></TotalsTable></TotalsSection>
      </FormCard>
      <FormCard>
        <SectionTitle>Notes</SectionTitle>
        <FormGrid><FormGroup $span2><TextArea value={formData.notes} onChange={e => handleHeaderChange('notes', e.target.value)} placeholder="Additional notes..." rows={3} /></FormGroup></FormGrid>
      </FormCard>
      <AttachmentPanel transactionId={editingRecord?.id} attachment={editingRecord?.attachment} canEdit={canEdit}
        onChange={({ attachment }) => setEditingRecord(prev => prev ? { ...prev, attachment } : prev)} />
    </>
  );

  useDocumentShortcuts({
    onNew: () => { if (!canEdit) { alert('You do not have permission to create.'); return; } handleCreate(); },
    onSave: () => { if (!canEdit) { alert('You do not have permission to save changes.'); return; } handleSave('Posted'); },
    viewMode, saving
  });

  return (
    <PageLayout pageTitle="Cash Book" breadcrumb="Journal / Cash Book">
      {viewMode === 'list' && renderListView()}
      {viewMode === 'form' && renderFormView()}
      {viewMode === 'view' && renderViewMode()}
    </PageLayout>
  );
};

const PartyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
`;

export default CashBook;
